import React, { useState } from "react";
import { getAsrCreditCard } from "../../services/api";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import { toDDMMYYYY } from "../../utils/dateUtils";

function todayISO() { return new Date().toISOString().split("T")[0]; }

const inputCls = "border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary";

const AsrCreditCard: React.FC = () => {
  const [ticketingProvider, setTicketingProvider] = useState("");
  const [stationNumber, setStationNumber]         = useState("");
  const [employeeNumber, setEmployeeNumber]       = useState("");
  const [reportDate, setReportDate]               = useState(todayISO());

  const [loading, setLoading]       = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [result, setResult]         = useState<any | null>(null);

  const canFetch = ticketingProvider.trim() && stationNumber.trim() && employeeNumber.trim() && reportDate;

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    setFetchError(null);
    setResult(null);
    try {
      const data = await getAsrCreditCard({
        ticketingProvider: ticketingProvider.trim().toUpperCase(),
        stationNumber: stationNumber.trim(),
        employeeNumber: employeeNumber.trim(),
        reportDate,
      });
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch credit card report."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">ASR: Credit Card</h1>
        <p className="text-sm text-gray-500 mt-1">
          Credit card transactions for an agent/station via Sabre TKT_AsrServicesRQ CreditCardRQ.
          Requires SabreSonic airline access.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ticketing Provider *</label>
            <input value={ticketingProvider} onChange={(e) => setTicketingProvider(e.target.value.toUpperCase())}
              placeholder="e.g. 7F" maxLength={2} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Station Number *</label>
            <input value={stationNumber} onChange={(e) => setStationNumber(e.target.value)}
              placeholder="8-digit station" maxLength={10} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Employee Number *</label>
            <input value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)}
              placeholder="6-digit employee" maxLength={10} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Report Date *</label>
            <DatePickerWithValidation
              selected={reportDate}
              onSelect={setReportDate}
              maxDate={todayISO()}
              placeholder="dd/mm/yyyy"
            />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={handleFetch} disabled={loading || !canFetch}
            className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50">
            {loading ? "Loading…" : "Fetch Report"}
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">{fetchError}</div>
      )}

      {result && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex flex-wrap gap-6 mb-4 text-sm">
            <div><span className="text-gray-500 text-xs uppercase font-semibold">Report Date</span><p className="font-semibold text-heading">{toDDMMYYYY(result.reportDate)}</p></div>
            <div><span className="text-gray-500 text-xs uppercase font-semibold">Station</span><p className="font-semibold text-heading">{result.stationNumber}</p></div>
          </div>
          <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-2">Raw Response</h3>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-700 overflow-auto max-h-[500px] whitespace-pre-wrap break-all">
            {JSON.stringify(result.rawData ?? result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AsrCreditCard;
